import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { OmitStylesComponentProps } from '../../types';
import { cva, VariantProps } from 'class-variance-authority';

export const checkboxVariants = cva(
    'rocco-checkbox [state=checked]:text-white peer shrink-0 overflow-hidden border text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            color: {
                default:
                    'border-gray-900 data-[state=checked]:bg-gray-900 data-[state=checked]:fill-white',
                brick: 'border-brick-600 data-[state=checked]:bg-brick-500 data-[state=checked]:text-primary',
            },
            size: {
                default: 'h-4 w-4',
                small: 'h-2.5 w-2.5',
            },
            shape: {
                default: 'rounded-sm',
                round: 'rounded-full',
            },
            groupHover: {
                light: 'group-hover:border-white group-hover:[&[data-state=checked]]:bg-white group-hover:[&[data-state=checked]]:text-black',
                default: '',
            },
            backgroundColor: {
                default: '',
                gray: 'bg-gray-200',
            },
            checkedBackgroundColor: {
                default:
                    '[data-state=checked]:bg-black data-[state=indeterminate]:bg-black',
                brick: '[data-state=checked]:bg-brick-500 data-[state=indeterminate]:bg-brick-500',
            },
            borderColor: {
                default: 'border-gray-900',
                brick: 'border-brick-600',
                none: 'border-transparent',
            },
        },
        defaultVariants: {
            color: 'default',
            size: 'default',
            shape: 'default',
            groupHover: 'default',
            backgroundColor: 'default',
            checkedBackgroundColor: 'default',
            borderColor: 'default',
        },
    }
);

export const checkboxIconVariants = cva('', {
    variants: {
        size: {
            default: 'h-4 w-4',
            small: 'h-2.5 w-2.5',
        },
        iconColor: {
            white: 'text-white',
            black: 'text-black',
        },
    },
    defaultVariants: {
        size: 'default',
        iconColor: 'white',
    },
});

export const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    OmitStylesComponentProps<typeof CheckboxPrimitive.Root> &
        VariantProps<typeof checkboxVariants> &
        VariantProps<typeof checkboxIconVariants>
>(
    (
        {
            color,
            size,
            shape,
            groupHover,
            iconColor,
            backgroundColor,
            checkedBackgroundColor,
            borderColor,
            ...props
        },
        ref
    ) => (
        <CheckboxPrimitive.Root
            ref={ref}
            className={checkboxVariants({
                color,
                size,
                shape,
                groupHover,
                backgroundColor,
                checkedBackgroundColor,
                borderColor,
            })}
            {...props}
        >
            <CheckboxPrimitive.Indicator
                className={'flex items-center justify-center text-current'}
            >
                {shape !== 'round' && props.checked !== 'indeterminate' && (
                    <Check
                        className={checkboxIconVariants({
                            size,
                            iconColor,
                        })}
                    />
                )}
                {shape !== 'round' && props.checked === 'indeterminate' && (
                    <Minus
                        className={checkboxIconVariants({
                            size,
                            iconColor,
                        })}
                    />
                )}
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    )
);

Checkbox.displayName = CheckboxPrimitive.Root.displayName;
